import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import {
  Box,
  Divider,
  IconButton,
  Icons,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import { useSelector } from 'react-redux';
import { getHomeViewRole } from '@app/selectors/home';
import { OutlookThread, OutlookThreadMessage } from './types';
import OutlookThreadMessageCard from './components/OutlookThreadMessageCard';
import OutlookEmailDetailSkeleton from './components/OutlookEmailDetailSkeleton';
import { trackOutlookReplyClicked } from './outlookAnalytics';

type OutlookThreadDialogProps = {
  open: boolean;
  thread?: OutlookThread;
  targetMessageId?: string;
  isLoading?: boolean;
  onClose: () => void;
};

const OutlookThreadDialog = ({
  open,
  thread,
  targetMessageId,
  isLoading = false,
  onClose,
}: OutlookThreadDialogProps) => {
  const theme = useTheme();
  const homeViewRole = useSelector(getHomeViewRole);

  // Display messages newest-first — the hook stores them oldest-first
  const messages = thread?.messages ?? [];
  const orderedMessages = [...messages].reverse();

  const hasHighImportance = orderedMessages.some((m) => m.isImportant);

  const activeTargetId = targetMessageId || thread?.targetMessageId;

  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const messageRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  // Auto-expand the target message (or newest message) when dialog opens or thread finishes loading
  useEffect(() => {
    if (!open) {
      setExpandedIds([]);
    } else if (orderedMessages.length > 0) {
      const targetId =
        activeTargetId && orderedMessages.some((m) => m.id === activeTargetId)
          ? activeTargetId
          : orderedMessages[0].id;
      setExpandedIds([targetId]);
    }
  }, [open, thread?.id, activeTargetId, orderedMessages.length]);

  // Scroll to the target message card when thread loads and dialog is open
  useEffect(() => {
    if (!open || isLoading || !thread || orderedMessages.length === 0) return;

    const targetId =
      activeTargetId && orderedMessages.some((m) => m.id === activeTargetId)
        ? activeTargetId
        : orderedMessages[0]?.id;

    if (!targetId) return;

    const timer = setTimeout(() => {
      const targetElement = messageRefs.current[targetId];
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [open, thread?.id, activeTargetId, isLoading, orderedMessages.length]);

  const toggleMessage = (id: string) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleReply = (message: OutlookThreadMessage) => {
    const webLink = message.webLink;
    if (webLink) {
      trackOutlookReplyClicked(homeViewRole);
      window.open(webLink, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      aria-labelledby="outlook-thread-title"
      PaperProps={{
        sx: {
          maxWidth: '648px',
          width: { xs: '100%', sm: 'calc(100% - 64px)' },
          height: { xs: '100%', sm: '85vh' },
          m: { xs: 0, sm: 4 },
          borderRadius: { xs: 0, sm: 2 },
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          display: 'flex',

          flexDirection: 'column',
          overflow: 'hidden',
          backgroundImage: 'none',
        },
      }}
    >
      <DialogContent
        sx={{
          pt: 3,
          pr: 3,
          pb: 3,
          pl: 3,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          height: '100%',
          overflow: 'hidden',
          bgcolor: 'transparent',
          backgroundImage: 'none',
          gap: '10px',
        }}
      >
        {/* ── Close button ── */}
        <IconButton
          aria-label="Close email thread"
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            color: 'text.secondary',
          }}
        >
          <Icons.Close />
        </IconButton>

        {/* ── Loading Skeleton / Content ── */}
        {isLoading || !thread ? (
          <OutlookEmailDetailSkeleton />
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            flex={1}
            minHeight={0}
            gap={2}
          >
            {/* ── Thread subject + importance badge ── */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              sx={{
                mt: 2,
                pr: 4,
                flexWrap: 'wrap',
              }}
            >
              <Typography
                id="outlook-thread-title"
                variant="h6"
                fontWeight="medium"
                sx={{
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? '#FFFFFF' : 'text.primary',
                }}
              >
                {thread?.subject || '(No Subject)'}
              </Typography>

              {hasHighImportance ? (
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                  flexShrink={0}
                >
                  <PriorityHighIcon sx={{ color: '#D32F2F', fontSize: 18 }} />
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    sx={{ color: '#D32F2F' }}
                  >
                    High Importance
                  </Typography>
                </Box>
              ) : null}
            </Box>

            <Divider />

            {/* ── Scrollable message cards ── */}
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {orderedMessages.map((message) => (
                <Box
                  key={message.id}
                  ref={(el: HTMLDivElement | null) => {
                    messageRefs.current[message.id] = el;
                  }}
                >
                  <OutlookThreadMessageCard
                    message={message}
                    isExpanded={expandedIds.includes(message.id)}
                    onToggle={toggleMessage}
                    onReply={handleReply}
                  />
                  <Divider sx={{ marginTop: 1, marginBottom: 2 }} />
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OutlookThreadDialog;
